"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Section, Input, Label, Switch, Button } from "@/src/components";
import { SelectBlock } from "@/src/components/input/select-advanced";
import { Mail, Search, Eye, EyeOff, Lock } from "lucide-react";
import { InstallCommand } from "@/components/docs/install-command";

const typeOptions = [
  { label: "Text", value: "text" },
  { label: "Email", value: "email" },
  { label: "Password", value: "password" },
  { label: "Number", value: "number" },
  { label: "Search", value: "search" },
  { label: "URL", value: "url" },
];

export default function DocsComponentsInput() {
  const [type, setType] = React.useState("text");
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Input
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Text inputs allow users to enter and edit text. They're the foundation of
            form interactions.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/input" />
        </SectionCard>
      </Section>

      {/* Interactive Playground */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Playground</CardTitle>
            <CardDescription>Experiment with different input configurations</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Preview */}
          <div className="flex flex-col justify-center min-h-40 bg-muted/30 rounded-lg border-2 border-dashed p-6">
            <div className="space-y-2">
              <Label htmlFor="demo-input">Label</Label>
              <Input
                id="demo-input"
                type={type}
                placeholder="Placeholder text..."
                disabled={isDisabled}
                aria-invalid={hasError}
              />
              {hasError && (
                <p className="text-xs text-destructive">This field has an error</p>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <SelectBlock
              label="Type"
              value={typeOptions.find((o) => o.value === type) || null}
              onChange={(v) => setType((v as typeof typeOptions[0])?.value || "text")}
              options={typeOptions}
            />
            <div className="flex items-center gap-3">
              <Switch
                id="input-disabled"
                checked={isDisabled}
                onCheckedChange={setIsDisabled}
              />
              <Label htmlFor="input-disabled">Disabled</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="input-error"
                checked={hasError}
                onCheckedChange={setHasError}
              />
              <Label htmlFor="input-error">Has Error</Label>
            </div>
          </div>
        </div>

        {/* Generated Code */}
        <div className="mt-6 space-y-2">
          <Text className="text-sm font-medium">Code:</Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <code>
              {`<Input${type !== "text" ? ` type="${type}"` : ""} placeholder="Placeholder text..."${isDisabled ? " disabled" : ""}${hasError ? ' aria-invalid="true"' : ""} />`}
            </code>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Basic Examples */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Basic Examples</CardTitle>
            <CardDescription>Common input patterns</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="email-input">Email</Label>
            <Input id="email-input" type="email" placeholder="you@example.com" />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password-input">Password</Label>
            <div className="relative">
              <Input
                id="password-input"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                style={{ paddingInlineEnd: "2.5rem" }}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="search-input">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input id="search-input" type="search" placeholder="Search..." style={{ paddingInlineStart: "2.25rem" }} />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="number-input">Amount</Label>
            <Input id="number-input" type="number" placeholder="0.00" />
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* With Icons */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>With Icons</CardTitle>
            <CardDescription>Inputs with leading or trailing icons</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Leading Icon</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="email" placeholder="Email address" style={{ paddingInlineStart: "2.25rem" }} />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Trailing Icon</Label>
            <div className="relative">
              <Input type="password" placeholder="Password" style={{ paddingInlineEnd: "2.5rem" }} />
              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* States */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>States</CardTitle>
            <CardDescription>Different input states</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Default</Label>
            <Input placeholder="Default input" />
          </div>
          
          <div className="space-y-2">
            <Label>Focused</Label>
            <Input placeholder="Click to focus" autoFocus />
          </div>
          
          <div className="space-y-2">
            <Label>Disabled</Label>
            <Input placeholder="Disabled input" disabled />
          </div>
          
          <div className="space-y-2">
            <Label>With Error</Label>
            <Input placeholder="Invalid input" aria-invalid="true" />
            <p className="text-xs text-destructive">This field is required</p>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Form Field Pattern */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Form Field Pattern</CardTitle>
            <CardDescription>Complete form field with label, hint, and error</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="max-w-md space-y-4">
          <div className="space-y-2">
            <Label htmlFor="complete-field">
              Email address
              <div className="text-destructive ml-1">*</div>
            </Label>
            <Input
              id="complete-field"
              type="email"
              placeholder="you@example.com"
              aria-describedby="complete-field-hint"
            />
            <p id="complete-field-hint" className="text-xs text-muted-foreground">
              We'll never share your email with anyone else
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="error-field">
              Username
              <div className="text-destructive ml-1">*</div>
            </Label>
            <Input
              id="error-field"
              placeholder="username"
              aria-invalid="true"
              aria-describedby="error-field-error"
            />
            <p id="error-field-error" role="alert" className="text-xs text-destructive">
              Username must be at least 3 characters
            </p>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Props Table */}
      <Section>
        <SectionCard
        header={<CardTitle>Props</CardTitle>}
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Prop</th>
              <th className="text-left py-2 font-semibold">Type</th>
              <th className="text-left py-2 font-semibold">Default</th>
              <th className="text-left py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">type</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
              <td className="py-2 font-mono text-xs">"text"</td>
              <td className="py-2 text-muted-foreground">Input type (text, email, password, etc.)</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">placeholder</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
              <td className="py-2 font-mono text-xs">-</td>
              <td className="py-2 text-muted-foreground">Placeholder text</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">disabled</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 font-mono text-xs">false</td>
              <td className="py-2 text-muted-foreground">Disable the input</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">aria-invalid</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 font-mono text-xs">false</td>
              <td className="py-2 text-muted-foreground">Mark input as invalid</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>

      {/* Accessibility */}
      <Section>
        <SectionCard
        header={<CardTitle>Accessibility</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <ul className="space-y-2 text-sm">
          <li>• <strong>Label:</strong> Always associate inputs with a Label via htmlFor/id</li>
          <li>• <strong>Required:</strong> Mark required fields visually and with aria-required</li>
          <li>• <strong>Errors:</strong> Link error messages with aria-describedby</li>
          <li>• <strong>Invalid:</strong> Use aria-invalid="true" for invalid inputs</li>
          <li>• <strong>Hints:</strong> Associate help text with aria-describedby</li>
        </ul>
      </SectionCard>
      </Section>

      {/* Guidelines */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard
          header={<CardTitle className="text-success">✓ Do</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Always use a visible label</li>
            <li>• Provide helpful placeholder text</li>
            <li>• Show clear error messages</li>
            <li>• Use appropriate input types</li>
            <li>• Mark required fields clearly</li>
          </ul>
        </SectionCard>

        <SectionCard
          header={<CardTitle className="text-destructive">✗ Don't</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Don't use placeholder as label</li>
            <li>• Don't hide required indicators</li>
            <li>• Don't show vague error messages</li>
            <li>• Don't disable without reason</li>
            <li>• Don't forget focus styles</li>
          </ul>
        </SectionCard>
        </div>
      </Section>
    </>
  );
}
